import { useEffect, useState } from "react";
import {
  Button,
  Container,
  Form,
  Modal,
  OverlayTrigger,
  Spinner,
  Tooltip,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import { useTranslate } from "@tolgee/react";

import { fetchPosts, setPageNumber } from "../../../redux/slices/postIdeas";
import { PostIdeasListDataTable } from "../../../components/postIdeas/postIdeasDatatable";
import { LomavisFeatureAvailable1 } from "../../../lomavisSharedUi/atoms/links/LomavisFeatureAvailable1";
import { LomavisCopyableText } from "../../../lomavisSharedUi/atoms/typography/LomavisCopyableText/LomavisCopyableText";
import axiosClient from "../../../utils/axios";
import { resolve } from "../../../api/endPoints";

type ContentCollectionConfiguration = {
  enabled: boolean;
  public_uuid: string;
  public_url: string;
};

const LocationCollectedContentList = () => {
  const { locationslug } = useParams();
  const dispatch = useDispatch();
  const { t } = useTranslate();
  const { page } = useSelector((state: any) => state.postIdeas);
  const [paginationScope, setPaginationScope] = useState<string | undefined>();

  const [configuration, setConfiguration] =
    useState<ContentCollectionConfiguration | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const [isFeatureAvailable, setIsFeatureAvailable] = useState(false);
  const [isLoadingConfiguration, setIsLoadingConfiguration] = useState(true);
  const [isSavingConfiguration, setIsSavingConfiguration] = useState(false);
  const [isRegeneratingLink, setIsRegeneratingLink] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showRegenerateConfirmationModal, setShowRegenerateConfirmationModal] =
    useState(false);

  useEffect(() => {
    const loadSubscription = async () => {
      if (!locationslug) {
        return;
      }

      setIsLoadingSubscription(true);
      try {
        const response = await axiosClient.get(
          resolve("current_subscription", {}, {})
        );
        setIsFeatureAvailable(
          !!response.data?.social_media_management_subscription
            ?.content_collection_via_link
        );
      } catch (error) {
        console.error("Failed to load collected content subscription data", error);
        setIsFeatureAvailable(false);
      } finally {
        setIsLoadingSubscription(false);
      }
    };

    void loadSubscription();
  }, [locationslug]);

  useEffect(() => {
    if (!locationslug) {
      return;
    }

    if (isLoadingSubscription || !isFeatureAvailable) {
      return;
    }

    if (paginationScope !== locationslug) {
      setPaginationScope(locationslug);
      if (page !== 1) {
        dispatch(setPageNumber(1));
      }
      return;
    }

    dispatch(fetchPosts(page, { location__slug: locationslug }) as any);
  }, [
    dispatch,
    isFeatureAvailable,
    isLoadingSubscription,
    locationslug,
    page,
    paginationScope,
  ]);

  useEffect(() => {
    const loadConfiguration = async () => {
      if (!locationslug) {
        return;
      }

      if (isLoadingSubscription || !isFeatureAvailable) {
        return;
      }

      setIsLoadingConfiguration(true);
      try {
        const response = await axiosClient.get(
          resolve("cockpit_content_collection_configuration", { locationslug }, {})
        );
        setConfiguration(response.data);
        setEnabled(response.data.enabled);
      } catch {
        setConfiguration(null);
        setEnabled(false);
        toast.error(t("locationCollectedContent.loadFailed"), {
          position: "top-right",
        });
      } finally {
        setIsLoadingConfiguration(false);
      }
    };

    void loadConfiguration();
  }, [isFeatureAvailable, isLoadingSubscription, locationslug, t]);

  const copyLink = async () => {
    if (!configuration?.public_url) {
      return;
    }

    try {
      await navigator.clipboard.writeText(configuration.public_url);
      toast.success(t("locationCollectedContent.linkCopied"), {
        position: "top-right",
      });
    } catch {
      toast.error(t("locationCollectedContent.copyFailed"), {
        position: "top-right",
      });
    }
  };

  const saveConfiguration = async () => {
    if (!locationslug) {
      return;
    }

    setIsSavingConfiguration(true);
    try {
      const response = await axiosClient.patch(
        resolve("cockpit_content_collection_configuration", { locationslug }, {}),
        { enabled }
      );
      setConfiguration(response.data);
      setEnabled(response.data.enabled);
      setShowSettingsModal(false);
      toast.success(t("locationCollectedContent.settingsUpdated"), {
        position: "top-right",
      });
    } catch {
      if (configuration) {
        setEnabled(configuration.enabled);
      }
      toast.error(t("locationCollectedContent.settingsUpdateFailed"), {
        position: "top-right",
      });
    } finally {
      setIsSavingConfiguration(false);
    }
  };

  const regenerateLink = async () => {
    if (!locationslug) {
      return;
    }

    setIsRegeneratingLink(true);
    try {
      const response = await axiosClient.post(
        resolve(
          "cockpit_content_collection_configuration_regenerate",
          { locationslug },
          {}
        )
      );
      setConfiguration(response.data);
      setShowRegenerateConfirmationModal(false);
      toast.success(t("locationCollectedContent.linkRegenerated"), {
        position: "top-right",
      });
    } catch {
      toast.error(t("locationCollectedContent.linkRegenerateFailed"), {
        position: "top-right",
      });
    } finally {
      setIsRegeneratingLink(false);
    }
  };

  const copyLinkDisabledReason = isLoadingConfiguration
    ? t("locationCollectedContent.copyLinkDisabledLoading")
    : !configuration?.public_url
      ? t("locationCollectedContent.copyLinkDisabledUnavailable")
      : !configuration?.enabled
        ? t("locationCollectedContent.copyLinkDisabledEnableFirst")
        : null;

  const navBarHeight =
    (document.getElementById("kt_app_header")?.clientHeight || 0) + 15 * 2 + 50;

  if (isLoadingSubscription) {
    return (
      <Container
        style={{
          height: `calc(100vh - ${navBarHeight}px)`,
        }}
        className="w-100"
      >
        <Toaster />
        <div className="w-100 h-100 d-flex justify-content-center align-items-center">
          <div className="w-100 h-50 bg-white rounded p-2 d-flex flex-column gap-3 justify-content-center align-items-center">
            <Spinner animation="border" role="status" />
            <div className="small text-muted">
              {t(
                "locationCollectedContent.loadingSubscription",
                "Checking subscription…"
              )}
            </div>
          </div>
        </div>
      </Container>
    );
  }

  const featureInfo = (
    <div className="card p-4 rounded mb-4">
      <div className="d-flex flex-row align-items-start gap-3 mb-4">
        <div
          className="d-flex align-items-center justify-content-center rounded-circle bg-light-primary text-primary flex-shrink-0"
          style={{ width: 48, height: 48, fontSize: 22 }}
        >
          <i className="fa-duotone fa-share-nodes"></i>
        </div>
        <div className="d-flex flex-column">
          <h3 className="mb-1 fw-bold">
            {t("locationCollectedContent.intro.title")}
          </h3>
          <p className="text-muted mb-0">
            {t("locationCollectedContent.intro.description")}
          </p>
        </div>
      </div>

      <div className="row g-3 mb-4">
        {[1, 2, 3].map((step) => (
          <div className="col-12 col-md-4" key={step}>
            <div className="d-flex flex-row align-items-start gap-3 h-100 p-3 rounded bg-light">
              <div
                className="d-flex align-items-center justify-content-center rounded-circle bg-primary text-white fw-bold flex-shrink-0"
                style={{ width: 28, height: 28, fontSize: 13 }}
              >
                {step}
              </div>
              <div>
                <div className="fw-semibold mb-1">
                  {t(`locationCollectedContent.intro.step${step}Title`)}
                </div>
                <div className="small text-muted">
                  {t(`locationCollectedContent.intro.step${step}Description`)}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {isFeatureAvailable && (
        <div className="d-flex flex-row justify-content-end gap-2">
          {copyLinkDisabledReason ? (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>{copyLinkDisabledReason}</Tooltip>}
            >
              <span className="d-inline-flex">
                <Button
                  variant="primary"
                  className="btn-sm d-flex align-items-center"
                  onClick={() => {
                    void copyLink();
                  }}
                  disabled={true}
                >
                  <i className="fa-duotone fa-copy me-2"></i>
                  {t("locationCollectedContent.copyLink")}
                </Button>
              </span>
            </OverlayTrigger>
          ) : (
            <Button
              variant="primary"
              className="btn-sm d-flex align-items-center"
              onClick={() => {
                void copyLink();
              }}
              disabled={
                !configuration?.public_url ||
                !configuration?.enabled ||
                isLoadingConfiguration
              }
            >
              <i className="fa-duotone fa-copy me-2"></i>
              {t("locationCollectedContent.copyLink")}
            </Button>
          )}
          <Button
            variant="white"
            className="btn-sm d-flex align-items-center"
            onClick={() => setShowSettingsModal(true)}
            disabled={isLoadingConfiguration}
          >
            <i className="fa-duotone fa-gear me-2"></i>
            {t("settings")}
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <Container className="w-100">
      <Toaster />
      <LomavisFeatureAvailable1
        isAvailable={isFeatureAvailable}
        info={featureInfo}
        title={t(
          "locationCollectedContent.notAvailable",
          "This feature is not available in your current subscription."
        )}
      >
        <div className="card p-2 rounded">
          <PostIdeasListDataTable query={{ location__slug: locationslug }} />
        </div>
      </LomavisFeatureAvailable1>

      <Modal
        show={showSettingsModal}
        onHide={() => {
          setShowSettingsModal(false);
          setEnabled(configuration?.enabled || false);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("locationCollectedContent.settingsTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoadingConfiguration ? (
            <div className="py-5 text-center">
              <Spinner animation="border" size="sm" />
            </div>
          ) : (
            <div className="d-flex flex-column gap-4">
              <div className="small text-muted">
                {t("locationCollectedContent.intro.description")}
              </div>

              <Form.Group>
                <Form.Label>{t("locationCollectedContent.publicLink")}</Form.Label>
                <LomavisCopyableText
                  text={configuration?.public_url || ""}
                  showBackground={true}
                />
                <Form.Text className="text-muted">
                  {t("locationCollectedContent.publicLinkHelp")}
                </Form.Text>
              </Form.Group>

              <Form.Group>
                <Form.Check
                  type="switch"
                  id="location-content-collection-enabled"
                  label={t("locationCollectedContent.enableSharedLink")}
                  checked={enabled}
                  onChange={(event) => setEnabled(event.target.checked)}
                />
                <Form.Text className="text-muted">
                  {t("locationCollectedContent.enableSharedLinkHelp")}
                </Form.Text>
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setShowRegenerateConfirmationModal(true);
            }}
            disabled={isLoadingConfiguration || isRegeneratingLink}
          >
            <i className="fa-duotone fa-rotate-right me-2"></i>
            {isRegeneratingLink
              ? t("locationCollectedContent.regenerating")
              : t("locationCollectedContent.regenerateLink")}
          </Button>

          <div className="d-flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setShowSettingsModal(false);
                setEnabled(configuration?.enabled || false);
              }}
            >
              {t("close")}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                void saveConfiguration();
              }}
              disabled={isLoadingConfiguration || isSavingConfiguration}
            >
              {isSavingConfiguration
                ? t("locationCollectedContent.saving")
                : t("save")}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showRegenerateConfirmationModal}
        onHide={() => setShowRegenerateConfirmationModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t("locationCollectedContent.regenerateConfirmationTitle")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("locationCollectedContent.regenerateConfirmationMessage")}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowRegenerateConfirmationModal(false)}
            disabled={isRegeneratingLink}
          >
            {t("close")}
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => {
              void regenerateLink();
            }}
            disabled={isRegeneratingLink}
          >
            {isRegeneratingLink
              ? t("locationCollectedContent.regenerating")
              : t("locationCollectedContent.regenerateConfirm")}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default LocationCollectedContentList;
